import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Pencil, Filter, RefreshCw, Search as SearchIcon } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { ADMIN_STATUS, STATUS_LABEL, STATUS_TONE } from "@/data/adminMock";
import { useOpportunities } from "@/hooks/useOpportunities";
import {
  adminListOpportunities,
  adminPublishOpportunity,
  adminRejectOpportunity,
} from "@/services/adminService";
import { ADMIN } from "@/constants/testIds";

const FILTER_TABS = [
  { key: "all",       label: "الكل" },
  { key: ADMIN_STATUS.PENDING,   label: "قيد المراجعة" },
  { key: ADMIN_STATUS.PUBLISHED, label: "منشورة" },
  { key: ADMIN_STATUS.REJECTED,  label: "مرفوضة" },
];

const ScoreCell = ({ value }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-ink-900">
    <span
      aria-hidden
      className={`h-1.5 w-1.5 rounded-full ${
        value >= 85 ? "bg-emerald-500" : value >= 70 ? "bg-amber-500" : "bg-red-500"
      }`}
    />
    {value}
  </span>
);

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `قبل ${mins} د`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `قبل ${hrs} س`;
  const days = Math.round(hrs / 24);
  return `قبل ${days} يوماً`;
};

export default function OpportunityReviewPage() {
  const { refresh: refreshPublic } = useOpportunities();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("status") || ADMIN_STATUS.PENDING;
  const [activeTab, setActiveTab] = useState(initial);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [query, setQuery] = useState(searchParams.get("q") || "");

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const list = await adminListOpportunities();
      setRows(list);
    } catch (e) {
      setLoadError(e);
      toast.error("تعذّر تحميل طابور المراجعة", { description: String(e?.message || e) });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Sync search query to URL so admin can share/bookmark filtered views.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (query.trim()) next.set("q", query.trim()); else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [query, searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    const byTab = activeTab === "all" ? rows : rows.filter((r) => r.status === activeTab);
    const q = query.trim().toLowerCase();
    if (!q) return byTab;
    return byTab.filter((r) =>
      [r.title, r.type, r.country, r.industry].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [rows, activeTab, query]);

  const setTab = (key) => {
    setActiveTab(key);
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("status");
    else next.set("status", key);
    setSearchParams(next, { replace: true });
  };

  const changeStatus = async (id, action, verb) => {
    setPendingId(id);
    try {
      if (action === "publish") await adminPublishOpportunity(id);
      else await adminRejectOpportunity(id);
      // Optimistic patch — then refresh authoritatively from the server.
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: action === "publish" ? ADMIN_STATUS.PUBLISHED : ADMIN_STATUS.REJECTED } : r)),
      );
      toast.success(`تم ${verb} الفرصة بنجاح`);
      // Public site depends on published rows — invalidate its cache.
      refreshPublic();
    } catch (e) {
      toast.error(`تعذّر ${verb} الفرصة`, { description: String(e) });
      loadRows(); // re-sync on error
    } finally {
      setPendingId(null);
    }
  };

  return (
    <AdminLayout>
      <section className="space-y-6">
        {/* Page header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
              التحرير
            </p>
            <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
              طابور المراجعة
            </h2>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
              راجع الفرص المُرشَّحة من محرِّك FIE والمصادر الخارجية، ثم انشرها أو ارفضها.
            </p>
          </div>
        </header>

        {/* Segmented tabs · larger surface, animated indicator via background */}
        <div className="flex flex-wrap items-center gap-3">
          <div
            data-testid={ADMIN.reviewTabs}
            role="tablist"
            className="inline-flex items-center gap-1 rounded-xl border border-surface-200 bg-white p-1 shadow-card"
          >
            {FILTER_TABS.map((t) => {
              const active = t.key === activeTab;
              const count = t.key === "all"
                ? rows.length
                : rows.filter((r) => r.status === t.key).length;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.key)}
                  data-testid={ADMIN.reviewTab(t.key)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                    active
                      ? "bg-fursa-700 text-white shadow-sm"
                      : "text-ink-700 hover:bg-surface-100"
                  }`}
                >
                  {t.key === "all" && <Filter className="h-3.5 w-3.5" strokeWidth={2} />}
                  {t.label}
                  <span
                    dir="ltr"
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded px-1.5 font-mono text-[10px] font-bold tabular-nums ${
                      active ? "bg-white/25 text-white" : "bg-surface-100 text-ink-700"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Action toolbar — search + refresh */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-200 bg-white p-3 shadow-card">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon
              className="pointer-events-none absolute inset-y-0 end-3 my-auto h-4 w-4 text-ink-500"
              strokeWidth={1.75}
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ابحث في الطابور بالعنوان، النوع، الدولة، أو القطاع…"
              data-testid={ADMIN.reviewSearch}
              className="h-10 w-full rounded-lg border border-surface-200 bg-surface-50 ps-3 pe-10 text-sm text-ink-900 outline-none transition-all placeholder:text-ink-500/60 hover:border-fursa-300 focus:border-fursa-500 focus:bg-white focus:ring-2 focus:ring-fursa-500/15"
            />
          </div>
          <div className="text-xs text-ink-500">
            <span dir="ltr" className="font-mono font-bold tabular-nums text-fursa-700">
              {filtered.length}
            </span>{" "}
            نتيجة
          </div>
          <div className="ms-auto flex items-center gap-2">
            <button
              type="button"
              onClick={loadRows}
              disabled={isLoading}
              data-testid={ADMIN.reviewRefresh}
              className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-xs font-semibold text-ink-700 transition-colors hover:border-fursa-300 hover:text-fursa-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} strokeWidth={1.75} />
              تحديث
            </button>
          </div>
        </div>

        {/* Table (desktop) */}
        <div className="hidden overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card lg:block">
          <table
            data-testid={ADMIN.reviewTable}
            className="w-full text-right"
          >
            <thead className="border-b border-surface-200 bg-surface-50 text-[10px] uppercase tracking-widest text-ink-500">
              <tr>
                <th className="px-5 py-3.5 text-start font-semibold">العنوان</th>
                <th className="px-5 py-3.5 font-semibold">النوع</th>
                <th className="px-5 py-3.5 font-semibold">الدولة</th>
                <th className="px-5 py-3.5 font-semibold">القطاع</th>
                <th className="px-5 py-3.5 font-semibold">النقاط</th>
                <th className="px-5 py-3.5 font-semibold">الحالة</th>
                <th className="px-5 py-3.5 font-semibold">آخر تحديث</th>
                <th className="px-5 py-3.5 text-center font-semibold">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200 text-sm">
              {isLoading && rows.length === 0 && (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} data-testid="review-skeleton-row">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-5 py-4">
                        <div className="skeleton h-3.5 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {!isLoading && loadError && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center">
                    <div
                      data-testid={ADMIN.reviewError}
                      role="alert"
                      className="mx-auto flex max-w-md flex-col items-center gap-3"
                    >
                      <XCircle className="h-9 w-9 text-risk-700" strokeWidth={1.5} aria-hidden />
                      <p className="text-sm font-semibold text-ink-900">تعذّر تحميل طابور المراجعة</p>
                      <p className="text-[12.5px] text-ink-500">تحقّق من الاتصال ثم أعد المحاولة.</p>
                      <button
                        type="button"
                        onClick={loadRows}
                        data-testid={ADMIN.reviewErrorRetry}
                        className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-4 py-2 text-xs font-semibold text-white hover:bg-fursa-800"
                      >
                        <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                        إعادة المحاولة
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && !loadError && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center">
                    <div
                      data-testid={ADMIN.reviewEmpty}
                      className="mx-auto flex max-w-md flex-col items-center gap-2"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-50 ring-1 ring-surface-200">
                        <CheckCircle2 className="h-5 w-5 text-fursa-600" strokeWidth={1.5} aria-hidden />
                      </div>
                      <p className="text-sm font-semibold text-ink-900">
                        {query.trim()
                          ? "لا توجد نتائج تطابق بحثك"
                          : activeTab === ADMIN_STATUS.PENDING
                          ? "لا توجد فرص قيد المراجعة الآن"
                          : "لا توجد فرص في هذا التبويب"}
                      </p>
                      <p className="max-w-xs text-[12.5px] text-ink-500">
                        {query.trim()
                          ? "جرّب مصطلحاً آخر أو أزل الفلاتر."
                          : "طابور المراجعة نظيف. الفرص الجديدة ستظهر هنا فور قدومها من المحلّل."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={ADMIN.reviewRow(r.id)}
                  className={`group transition-colors hover:bg-fursa-50/40 ${pendingId === r.id ? "opacity-60" : ""}`}
                >
                  <td className="max-w-xs px-5 py-4">
                    <Link
                      to={`/admin/opportunities/${r.id}/edit`}
                      className="block truncate font-medium text-ink-900 transition-colors hover:text-fursa-700"
                      title={r.title}
                    >
                      {r.title}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-ink-700">{r.type}</td>
                  <td className="px-5 py-4 text-ink-700">{r.country}</td>
                  <td className="px-5 py-4 text-ink-700">{r.industry}</td>
                  <td className="px-5 py-4"><ScoreCell value={r.score} /></td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_TONE[r.status]}`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-ink-500">
                    {formatRelative(r.submittedAt)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => changeStatus(r.id, "publish", "نشر")}
                        disabled={r.status === ADMIN_STATUS.PUBLISHED || pendingId === r.id}
                        data-testid={ADMIN.actionPublish(r.id)}
                        title="نشر"
                        aria-label={`نشر الفرصة ${r.title}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
                      </button>
                      <Link
                        to={`/admin/opportunities/${r.id}/edit`}
                        data-testid={ADMIN.actionEdit(r.id)}
                        title="تعديل"
                        aria-label={`تعديل الفرصة ${r.title}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fursa-700 transition-colors hover:bg-fursa-50"
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.75} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => changeStatus(r.id, "reject", "رفض")}
                        disabled={r.status === ADMIN_STATUS.REJECTED || pendingId === r.id}
                        data-testid={ADMIN.actionReject(r.id)}
                        title="رفض"
                        aria-label={`رفض الفرصة ${r.title}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <XCircle className="h-4 w-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards (mobile / tablet) */}
        <div className="grid grid-cols-1 gap-3 lg:hidden">
          {isLoading && rows.length === 0 && (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={`m-sk-${i}`} data-testid="review-mobile-skeleton" className="rounded-xl border border-surface-200 bg-white p-4">
                <div className="skeleton h-4 w-3/4 rounded" />
                <div className="mt-3 flex gap-2">
                  <div className="skeleton h-3 w-16 rounded" />
                  <div className="skeleton h-3 w-16 rounded" />
                </div>
                <div className="mt-4 skeleton h-8 w-full rounded" />
              </div>
            ))
          )}
          {!isLoading && loadError && (
            <div className="rounded-xl border border-risk-100 bg-risk-50/40 p-6 text-center">
              <p className="text-sm font-semibold text-ink-900">تعذّر تحميل الطابور</p>
              <button
                type="button"
                onClick={loadRows}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-4 py-2 text-xs font-semibold text-white hover:bg-fursa-800"
              >
                <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                إعادة المحاولة
              </button>
            </div>
          )}
          {filtered.map((r) => (
            <article
              key={r.id}
              data-testid={ADMIN.reviewCard(r.id)}
              className={`rounded-xl border border-surface-200 bg-white p-4 ${pendingId === r.id ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-semibold text-ink-900">
                    {r.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                    <span>{r.type}</span>
                    <span>·</span>
                    <span>{r.country}</span>
                    <span>·</span>
                    <span>{r.industry}</span>
                  </div>
                </div>
                <ScoreCell value={r.score} />
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-surface-100 pt-3">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[r.status]}`}
                >
                  {STATUS_LABEL[r.status]}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => changeStatus(r.id, "publish", "نشر")}
                    disabled={r.status === ADMIN_STATUS.PUBLISHED || pendingId === r.id}
                    data-testid={`${ADMIN.actionPublish(r.id)}-mobile`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    نشر
                  </button>
                  <Link
                    to={`/admin/opportunities/${r.id}/edit`}
                    data-testid={`${ADMIN.actionEdit(r.id)}-mobile`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-intel-200 bg-intel-50 px-3 text-xs font-medium text-intel-800 transition-colors hover:bg-intel-100"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                    تعديل
                  </Link>
                  <button
                    type="button"
                    onClick={() => changeStatus(r.id, "reject", "رفض")}
                    disabled={r.status === ADMIN_STATUS.REJECTED || pendingId === r.id}
                    data-testid={`${ADMIN.actionReject(r.id)}-mobile`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <XCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
                    رفض
                  </button>
                </div>
              </div>
            </article>
          ))}
          {!isLoading && !loadError && filtered.length === 0 && (
            <div className="rounded-xl border border-dashed border-surface-200 bg-white px-4 py-12 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-50 ring-1 ring-surface-200">
                <CheckCircle2 className="h-5 w-5 text-fursa-600" strokeWidth={1.5} aria-hidden />
              </div>
              <p className="text-sm font-semibold text-ink-900">
                {query.trim() ? "لا توجد نتائج تطابق بحثك" : "لا توجد فرص في هذا التبويب"}
              </p>
            </div>
          )}
        </div>
      </section>
    </AdminLayout>
  );
}
